import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Badge } from '../components/ui/badge';
import { GitPullRequest } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StatusPill = ({ status }) => {
  const cfg = {
    pending:  { c: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/30',   l: 'Pending review' },
    approved: { c: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30', l: 'Approved & published' },
    rejected: { c: 'bg-red-500/15 text-red-300 border-red-500/30',             l: 'Rejected' },
  }[status] || { c: 'bg-[#262626] text-[#A3A3A3]', l: status };
  return <Badge className={`${cfg.c} border`}>{cfg.l}</Badge>;
};

const MyChanges = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/edits/mine`, { withCredentials: true });
        setItems(data.items || []);
      } catch { /* ignore */ }
      setLoading(false);
    };
    load();
  }, []);

  const grouped = items.reduce((acc, it) => {
    (acc[it.status] = acc[it.status] || []).push(it);
    return acc;
  }, {});

  return (
    <div className="p-6 space-y-6" data-testid="my-changes-page">
      <div>
        <h1 className="text-2xl font-semibold text-white flex items-center gap-2">
          <GitPullRequest className="w-6 h-6 text-[#D4AF37]" /> My Changes
        </h1>
        <p className="text-[#A3A3A3] text-sm mt-1">Track the status of edits you've submitted. Approved changes are published to the live record automatically.</p>
      </div>

      {loading ? (
        <div className="p-8 text-[#A3A3A3] text-center">Loading…</div>
      ) : items.length === 0 ? (
        <div className="bg-[#141414] border border-[#262626] rounded-md p-12 text-center text-[#A3A3A3]" data-testid="my-changes-empty">
          You haven't submitted any changes yet.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((it) => (
            <div key={it.id} data-testid={`my-edit-${it.id}`} className="bg-[#141414] border border-[#262626] rounded-md p-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className="bg-[#D4AF37]/20 text-[#D4AF37] border-[#D4AF37]/30">{it.collection_key}</Badge>
                    <StatusPill status={it.status} />
                  </div>
                  <p className="text-[#A3A3A3] text-xs mt-2">Submitted {new Date(it.created_at).toLocaleString()}</p>
                  {it.reviewed_at && (
                    <p className="text-[#A3A3A3] text-xs">
                      Reviewed by <span className="text-white">{it.reviewed_by_name || '—'}</span> · {new Date(it.reviewed_at).toLocaleString()}
                    </p>
                  )}
                  {it.note && <p className="text-white text-sm mt-2">Your note: <span className="text-[#A3A3A3]">"{it.note}"</span></p>}
                  {it.decision_note && <p className="text-white text-sm mt-1">Reviewer: <span className="text-[#A3A3A3]">"{it.decision_note}"</span></p>}
                </div>
                <span className="text-[#525252] text-xs font-mono">{it.record_id.slice(0, 10)}…</span>
              </div>
            </div>
          ))}
        </div>
      )}

      <p className="text-[#525252] text-xs">{Object.entries(grouped).map(([s, arr]) => `${arr.length} ${s}`).join(' · ')}</p>
    </div>
  );
};

export default MyChanges;
